import { createContext, useEffect, useReducer } from 'react';
import type { FC, ReactNode } from 'react';
import type { BackofficeUser } from '../../@types/user';
import { login, getUser, logout } from '../../api/authApi';
import { AUTH_STATUSES } from '../../consts';
import { mockPermissions } from '../../mock/permissions';
import { isMockPermissions } from '../../utils/environment';
import { ACTION_TYPES } from './actions';

interface State {
  status: string,
  isAuthenticated: boolean;
  user: BackofficeUser | null;
}

export interface AuthContextValue extends State {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermissions: (permissions: string[]) => boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

type Action = {
  type: string,
  payload: {
    isAuthenticated: boolean;
    user: BackofficeUser | null;
  }
}

const initialState: State = {
  status: AUTH_STATUSES.INITIALIZE,
  isAuthenticated: false,
  user: null,
};

const handlers: Record<string, (state: State, action: Action) => State> = {
  LOGIN: (state: State, action: Action): State => {
    const { user } = action.payload;

    return {
      ...state,
      status: AUTH_STATUSES.LOGGED_IN,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    status: AUTH_STATUSES.LOGGED_OUT,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state: State, action: Action): State => (
  handlers[action.type] ? handlers[action.type](state, action) : state
);

export const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  hasPermissions: () => false,
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        const user = await getUser();

        if (user) {
          dispatch({
            type: ACTION_TYPES.LOGIN,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: ACTION_TYPES.LOGOUT,
            payload: {
              user: null,
              isAuthenticated: false,
            },
          });
        }
      } catch (err) {
        dispatch({
          type: ACTION_TYPES.LOGOUT,
          payload: {
            user: null,
            isAuthenticated: false,
          },
        });
      }
    };

    initialize();
  }, []);

  const loginProvider = async (email: string, password: string): Promise<void> => {
    await login({ email, password });
    const user = await getUser();

    dispatch({
      type: ACTION_TYPES.LOGIN,
      payload: {
        user,
        isAuthenticated: true,
      },
    });
  };

  const logoutProvider = async (): Promise<void> => {
    await logout();
    dispatch({
      type: ACTION_TYPES.LOGOUT,
      payload: {
        user: null,
        isAuthenticated: false,
      },
    });
  };

  /**
   * Check if current user has permission(s)
   * @param permissions single string or array of string that represent required permissions
   * @returns boolean - true if has permission, false if not
   */
  const hasPermissionsProvider = (permissions: string[]): boolean => {
    if (!permissions || permissions.length <= 0) {
      return true;
    }

    const userPermissions = (isMockPermissions()) ?
      mockPermissions :
      (state.user?.permissions || []);

    return permissions.every((permission) => {
      return userPermissions.includes(permission);
    }) || false;
  };

  return (
    <AuthContext.Provider
      value={ {
        ...state,
        login: loginProvider,
        logout: logoutProvider,
        hasPermissions: hasPermissionsProvider,
      } }
    >
      {children}
    </AuthContext.Provider>
  );
};
